import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OverlayModal from '../../../components/ui/OverlayModal';
import type { RightToInformationCoverNoteResponse } from '../../../types/rightToInformationCoverNoteApi';
import { setFlashToast } from '../../../utils/flashToast';
import { formatDisplayDate, formatDisplayDateTime } from '../../../utils/dateFormat';
import { coverNoteQrImageSrc } from '../../../utils/coverNoteQr';
import { printCommonCoverNotePdf } from '../../../utils/commonCoverNotePrint';
import { criminalCoverNoteDivisionUnit } from '../../../utils/criminalCoverNoteDisplay';
import {
  downloadRightToInformationCoverNotePdf,
  getRightToInformationCoverNote,
  mergeRightToInformationCoverNoteResponse,
} from './rightToInformationCoverNote';

type RightToInformationCoverNoteModalProps = {
  isOpen: boolean;
  caseRequestId: string;
  fileRefFallback?: string;
  coverNoteFallback?: Partial<RightToInformationCoverNoteResponse>;
  showSuccessBanner?: boolean;
  onClose: () => void;
};

function displayValue(value: string | null | undefined): string {
  const t = value?.trim();
  return t || '—';
}

function CoverNoteSummaryField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="csf">
      <label>{label}</label>
      <p>{displayValue(value)}</p>
    </div>
  );
}

function declaredDocuments(documents: string[] | undefined): string[] {
  return (documents ?? []).map((d) => d.trim()).filter(Boolean);
}

function RightToInformationCoverNoteBody({
  coverNote,
  qrSrc,
}: {
  coverNote: RightToInformationCoverNoteResponse;
  qrSrc: string;
}) {
  const { t } = useTranslation();
  const documents = declaredDocuments(coverNote.documents);

  return (
    <div className="cn-body">
      <div className="cn-meta cn-meta-qf">
        <div className="cn-qr-box">
          {qrSrc ? (
            <img src={qrSrc} alt="Cover note QR code" width={68} height={68} style={{ display: 'block' }} />
          ) : (
            <div style={{ width: 68, height: 68, background: 'var(--bg)' }} aria-hidden />
          )}
          <div className="cn-qr-note">QR code for file intake, tracking and cover note verification.</div>
        </div>
        <div className="cn-box">
          <label>File Reference Number</label>
          <p>{displayValue(coverNote.fileReferenceNumber)}</p>
        </div>
        <div className="cn-box">
          <label>Request Submitted at</label>
          <p>{formatDisplayDateTime(coverNote.requestSubmittedAt) || '—'}</p>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.divisionUnit')}</label>
          <p>
            {displayValue(
              criminalCoverNoteDivisionUnit(coverNote.submittedDivisionName, coverNote.submittedAgency),
            )}
          </p>
        </div>
        <div className="cn-box">
          <label>File Category</label>
          <p>{displayValue(coverNote.fileCategory)}</p>
        </div>
      </div>

      <div className="cn-sum-title">Summary of Request Details</div>
      <div className="cn-sum-grid">
        <div className="cn-col">
          <CoverNoteSummaryField label="Name of Requester" value={coverNote.requestorName} />
          <CoverNoteSummaryField label="Public Authority Name" value={coverNote.specificPublicAuthorityName} />
          <CoverNoteSummaryField label="Urgency Level" value={coverNote.urgencyLevel} />
        </div>
        <div className="cn-col">
          <CoverNoteSummaryField label="Deadline" value={formatDisplayDate(coverNote.deadline) || null} />
          <CoverNoteSummaryField label="Urgency Justification" value={coverNote.urgencyJustification} />
          <div className="csf">
            <label>Documents Declared</label>
            {documents.length ? (
              <div className="dtags">
                {documents.map((doc, index) => (
                  <div key={`${doc}-${index}`} className="dtag">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {doc}
                  </div>
                ))}
              </div>
            ) : (
              <p className="ccr-review-val">—</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RightToInformationCoverNoteModal({
  isOpen,
  caseRequestId,
  fileRefFallback,
  coverNoteFallback,
  showSuccessBanner = false,
  onClose,
}: RightToInformationCoverNoteModalProps) {
  const { t } = useTranslation();
  const [coverNote, setCoverNote] = useState<RightToInformationCoverNoteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCoverNote(null);
      setLoading(false);
      return;
    }

    const id = caseRequestId.trim();
    if (!id) {
      setLoading(false);
      setFlashToast({ message: 'Case request id is missing.', variant: 'error' });
      return;
    }

    let mounted = true;
    setLoading(true);
    setCoverNote(null);

    void getRightToInformationCoverNote(id).then((r) => {
      if (!mounted) return;
      setLoading(false);
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Could not load cover note.', variant: 'error' });
        return;
      }
      setCoverNote(mergeRightToInformationCoverNoteResponse(r.data, coverNoteFallback));
    });

    return () => {
      mounted = false;
    };
  }, [isOpen, caseRequestId, coverNoteFallback]);

  const fileRef = coverNote?.fileReferenceNumber?.trim() || fileRefFallback?.trim() || '—';

  const downloadPdf = async () => {
    if (downloading || !coverNote) return;
    const id = caseRequestId.trim();
    if (!id) return;

    setDownloading(true);
    try {
      const r = await downloadRightToInformationCoverNotePdf(id);
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Cover note PDF could not be loaded.', variant: 'error' });
        return;
      }

      const pdfBlob =
        r.data.type === 'application/pdf' ? r.data : new Blob([r.data], { type: 'application/pdf' });
      const safeRef = fileRef.replace(/[^\w.-]+/g, '_');
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cover-Note-${safeRef}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const printCoverNote = async () => {
    if (!coverNote || printing) return;
    setPrinting(true);
    try {
      const r = await printCommonCoverNotePdf(caseRequestId, fileRef);
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Cover note PDF could not be loaded.', variant: 'error' });
      }
    } finally {
      setPrinting(false);
    }
  };

  const qrSrc = coverNote ? coverNoteQrImageSrc(coverNote.qr) : '';

  return (
    <OverlayModal
      isOpen={isOpen}
      overlayClassName="overlay"
      contentClassName="cn-mbox cn-mbox--cover"
      centerVertically
      onClose={onClose}
    >
      {showSuccessBanner ? (
        <div className="success-bar cn-cover-success">
          <div className="success-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            <div className="success-title">Submission successful</div>
            <div className="success-sub">
              Request reference <strong>{fileRef}</strong> has been created and timestamped. Attach the printed Cover
              Note before handing over the physical case file to AGD.
            </div>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="cn-scroll">
          <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
          <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
            {t('caseRequests.loadingCoverNote')}
          </div>
        </div>
      ) : !coverNote ? (
        <div className="cn-scroll">
          <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
          <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
            Cover note details are unavailable.
            <div style={{ marginTop: 16 }}>
              <button className="mfb mfb-dk" type="button" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="cn-printable cn-scroll">
            <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
            <RightToInformationCoverNoteBody coverNote={coverNote} qrSrc={qrSrc} />
          </div>
          <div className="cn-footer">
            <button className="mfb" type="button" disabled={downloading} onClick={() => void downloadPdf()}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {downloading ? 'Downloading…' : 'Download as PDF'}
            </button>
            <button className="mfb" type="button" disabled={printing} onClick={() => void printCoverNote()}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              {printing ? 'Preparing…' : 'Print'}
            </button>
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </>
      )}
    </OverlayModal>
  );
}
